import React, { useState } from 'react'
import { ORDER_TYPES, useOrder } from 'context/OrderContext'
import { Box, useMediaQuery, useTheme } from '@mui/material'
import { useSelector } from 'react-redux'
import { calculateDiscount, getUTCDateTime, } from 'utils/function'
import { useAddOrderItemsMutation, useCreateTableOrderMutation } from 'pages/orders/services'
import NewOrderItemPanelContainer from './container'
import NoOrderPanelContainer from 'components/OrderPanel/NoOrderContainer'
import { useNavigate } from 'react-router-dom'
import OrderItemChefAssign from 'components/Modal/OrderItemChefAssign'

const NewOrderItemPanel = ({ orderDetails }) => {
    const theme = useTheme()
    const navigate = useNavigate();
    const isDesktop = useMediaQuery(theme.breakpoints.up("lg"))
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
    const userData = useSelector((state) => state?.posData);

    const platformFeePercent = parseFloat(userData?.vendor?.platform_fee) || 0;
    const serviceTaxPercent = parseFloat(userData?.vendor?.service_tax) || 0;

    const { orderType, orderItems, setOrderItems, customerData, addItemToOrder, removeItemFromOrder, resetOrder, openDialog, closeDialog } = useOrder()

    const [addTableOrder, { isLoading }] = useAddOrderItemsMutation();

    // 🧩 Assign Chef modal state
    const [assignChefOpen, setAssignChefOpen] = useState(false);
    const [assignChefItems, setAssignChefItems] = useState([]);
    const [assignChefSelections, setAssignChefSelections] = useState({});
    const [loader, setLoader] = useState(false);
    const [resumeAfterChefAssign, setResumeAfterChefAssign] = useState(null);

    // 3️⃣ When user confirms cancellation…
    const handleConfirmCancel = () => {
        // actually reset the order
        resetOrder()
    }

    // Calculate totals
    const cartTotal = orderItems.reduce((total, item) => {
        const itemTotal = item.price * item.quantity
        const addOnsTotal = item.addOns ? item.addOns.reduce((sum, addon) => sum + addon.price, 0) : 0
        return total + itemTotal + addOnsTotal * item.quantity
    }, 0);

    const cartItemtax = (cartTotal * serviceTaxPercent) / 100;
    const cartItemplatformFee = 0; // POS orders: platform fee is absorbed by vendor, not charged to customer
    const cartItemAdditionalTotal = cartTotal + cartItemtax;


    const prevCartTotal = orderDetails?.order_items_total
        ? parseFloat(orderDetails?.order_items_total)
        : 0;

    // Combined cart total (previous + new)
    const combinedCartTotal = prevCartTotal + cartTotal;

    // Apply discount on combined total
    const discountAmount = calculateDiscount(
        combinedCartTotal,
        orderDetails?.discount_type,
        orderDetails?.discount_value
    );

    const afterDiscountTotal = combinedCartTotal - discountAmount;

    // Tax & Platform fee applied after discount
    const tax = (afterDiscountTotal * serviceTaxPercent) / 100;
    const platformFee = 0; // POS orders: fee absorbed by vendor

    // Final order total (for DB)
    const orderTotal = afterDiscountTotal + tax;

    // FE "additional total" → only for NEW cart items
    const newAfterDiscount = cartTotal - calculateDiscount(
        cartTotal,
        orderDetails?.discount_type,
        orderDetails?.discount_value
    );
    const additionalTotal = newAfterDiscount +
        (newAfterDiscount * serviceTaxPercent) / 100;

    // Format cart items for the API
    const formatCartItems = () => {
        return orderItems.map((item) => ({
            ...(item.isMisc ? {} : { id: item.id }),
            name: item.name,
            price: item.price ?? 0,
            image: item.image || null,
            // primary_chef_id: item.primary_chef,
            // primary_chef_id: item.isMisc ? null : (item?.station?.chefs?.length == 1 ? item?.station?.chefs[0]?.id : item?.primary_chef_id),
            primary_chef_id: item.isMisc
                ? (item.cookingRequired ? item.primary_chef_id : null)
                : (item?.station?.chefs?.length == 1 ? item?.station?.chefs[0]?.id : item?.primary_chef_id),
            station_id: item.isMisc
                ? (item.cookingRequired ? (item.station_id || null) : null)
                : item?.station?.id,
            quantity: item.quantity,
            total_price: (item.price ?? 0) * item.quantity,
            ...(item.notes && { notes: item.notes }),
            ...(item.isMisc && { isMiscellaneous: true }),
            ...(item.isMisc && item.cookingRequired && { cookingRequired: true }),
            ...(item.addOns &&
                item.addOns.length > 0 && {
                add_ons: item.addOns.map((addon) => ({
                    id: addon.id || Math.floor(Math.random() * 1000), // Fallback if id is missing
                    name: addon.name,
                    price: addon.price,
                })),
            }),
        }))
    }
    // Handle create order with API integration
    const handleCreateOrder = async () => {
        try {
            // ✅ 1️⃣ If order already "ready_to_prepare" → just continue
            if (orderDetails?.order_status == "ready_to_prepare" || orderDetails?.order_status == "accept") {
                return await submitOrder()
            }

            // const order_table_id = orderDetails?.id;
            // // Prepare order data according to backend requirements
            // const orderData = {
            //     tax_amount: serviceTaxPercent,
            //     order_items_total: combinedCartTotal,
            //     platform_fee: 0, // POS order: fee absorbed by vendor
            //     discount_type: orderDetails?.discount_type || null,
            //     discount_value: orderDetails?.discount_value || null,
            //     discount_amount: discountAmount,
            //     // discount_amount: discount,
            //     total_amount: orderTotal,
            //     cart: formatCartItems(),
            // }

            // // Call the API to create the order
            // // const response = await addTableOrder(orderData).unwrap()
            // const response = await addTableOrder({
            //     id: order_table_id,
            //     orderData,
            // }).unwrap()
            // if(response?.status == 200){
            //     navigate(-1);
            //     resetOrder();
            // }

            // Find items needing chef assignment
            const itemsNeedingChef = orderItems.filter(
                (item) =>
                    item?.station?.chefs &&
                    item.station.chefs.length > 1 &&
                    !item.primary_chef_id
            );

            const formattedItems = itemsNeedingChef?.map((itm) => ({
                menu_item_id: itm.id,
                menu_item_name: itm.name,
                stationData: itm.station,
            })) || [];


            if (itemsNeedingChef.length > 0) {
                setAssignChefItems(formattedItems);
                // setAssignChefSelections({});
                // ✅ Pre-select the first chef by default for each station
                const defaultSelections = {};
                formattedItems.forEach((itm) => {
                    const firstChef = itm.stationData?.chefs?.[0];
                    if (firstChef) defaultSelections[itm.menu_item_id] = firstChef.id;
                });
                setAssignChefSelections(defaultSelections);
                setAssignChefOpen(true);
                // setResumeAfterChefAssign(() => async () => await submitOrder());
                return;
            }

            // ✅ 3️⃣ If no multiple-chef stations, continue as usual
            await submitOrder()
        } catch (error) {
            console.error("Failed to create order:", error)
        }
    }

    // ♻️ Extracted Submit Order Function (so we can reuse)
    const submitOrder = async () => {
        const order_table_id = orderDetails?.id
        const orderData = {
            order_source: "pos",
            tax_amount: serviceTaxPercent,
            order_items_total: combinedCartTotal,
            platform_fee: 0,
            discount_type: orderDetails?.discount_type || null,
            discount_value: orderDetails?.discount_value || null,
            discount_amount: discountAmount,
            // discount_amount: discount,
            total_amount: orderTotal,
            cart: formatCartItems(),
        }

        const response = await addTableOrder({
            id: order_table_id,
            orderData,
        }).unwrap()

        if (response?.status === 200) {
            navigate(-1)
            resetOrder()
        }
    }

    const updateChefInOrderItems = (updatedItems) => {
        setOrderItems((prevItems) =>
            prevItems.map((item) => {
                const matched = updatedItems.find((u) => u.menu_item_id === item.id);
                return matched
                    ? { ...item, primary_chef_id: matched.primary_chef_id }
                    : item;
            })
        );
    };


    /** 🧩 Step 3: Handle chef selection */
    const handleAssignChefChange = (menu_item_id, chefId) => {
        setAssignChefSelections((prev) => ({
            ...prev,
            [menu_item_id]: chefId,
        }));
    };

    /** 🧩 Step 4: When modal submit clicked */
    const handleAssignChefSubmit = async () => {
        setLoader(true);
        try {
            const updatedItems = assignChefItems.map((itm) => {
                const chefId = assignChefSelections[itm.menu_item_id];
                const chef = itm.stationData?.chefs?.find((c) => c.id === chefId);
                return {
                    ...itm,
                    primary_chef_id: chef?.id || null,
                    selectedChef: chef || null,
                };
            });
            updateChefInOrderItems(updatedItems);

            setAssignChefOpen(false);
            setAssignChefItems([]);
            setAssignChefSelections({});
            setLoader(false);

            if (resumeAfterChefAssign) await resumeAfterChefAssign();
            setResumeAfterChefAssign(null);
        } catch (err) {
            console.error('Chef assignment error:', err);
            setLoader(false);
        }
    };

    // Handle item quantity change


    //     const handleAssignChefSubmit = async () => {
    //     setLoader(true);
    //     try {
    //         // Build updated item list with selected chefs
    //         const updatedItems = assignChefItems.map((itm) => {
    //         const chefId = assignChefSelections[itm.menu_item_id];
    //         const chef = itm.stationData?.chefs?.find((c) => c.id === chefId);
    //         return {
    //             ...itm,
    //             primary_chef_id: chef?.id || null,
    //             selectedChef: chef || null,
    //         };
    //         });

    //         console.log(updatedItems, "updatedItems after chef assign");
    //         updateChefInOrderItems(updatedItems);

    //         // ✅ Create a merged version of orderItems (without touching context)
    //         const mergedOrderItems = orderItems.map((itm) => {
    //         const match = updatedItems.find((u) => u.menu_item_id === itm.id);
    //         return match
    //             ? { ...itm, primary_chef_id: match.primary_chef_id }
    //             : itm;
    //         });
    //     // ✅ Use this merged version for order submission
    //     await submitOrderWithItems(mergedOrderItems);

    //     // Reset modal state
    //     setAssignChefOpen(false);
    //     setAssignChefItems([]);
    //     setAssignChefSelections({});
    //     setLoader(false);
    //     setResumeAfterChefAssign(null);
    //   } catch (err) {
    //     console.error("Chef assignment error:", err);
    //     setLoader(false);
    //   }
    //     };

    // const submitOrderWithItems = async (customItems) => {
    //   const order_table_id = orderDetails?.id;

    //   const orderData = {
    //     tax_amount: serviceTaxPercent,
    //     order_items_total: combinedCartTotal,
    //     platform_fee: 0,
    //     discount_type: orderDetails?.discount_type || null,
    //     discount_value: orderDetails?.discount_value || null,
    //     discount_amount: discountAmount,
    //     total_amount: orderTotal,
    //     cart: customItems.map((item) => ({
    //       id: item.id,
    //       name: item.name,
    //       price: item.price,
    //       image: item.image || "",
    //       primary_chef_id:
    //         item?.primary_chef_id ||
    //         (item?.station?.chefs?.length === 1 ? item.station.chefs[0]?.id : null),
    //       station_id: item?.station?.id,
    //       quantity: item.quantity,
    //       total_price: item.price * item.quantity,
    //       ...(item.addOns?.length > 0 && {
    //         add_ons: item.addOns.map((addon) => ({
    //           id: addon.id || Math.floor(Math.random() * 1000),
    //           name: addon.name,
    //           price: addon.price,
    //         })),
    //       }),
    //     })),
    //   };

    //   const response = await addTableOrder({
    //     id: order_table_id,
    //     orderData,
    //   }).unwrap();

    //   if (response?.status === 200) {
    //     navigate(-1);
    //     resetOrder();
    //   }
    // };

    const handleAddItem = (item) => {
        const itemToAdd = {
            ...item,
            quantity: 1,
        }
        addItemToOrder(itemToAdd)
    }

    const handleRemoveItem = (item) => {
        removeItemFromOrder(item.id, item.addOns, item.size)
    }


    return (
        <>
            {orderType ?
                <NewOrderItemPanelContainer
                    handleRemoveItem={handleRemoveItem}
                    handleAddItem={handleAddItem}
                    handleCreateOrder={handleCreateOrder}
                    userData={userData}
                    isLoading={isLoading}
                    theme={theme}
                    orderType={orderType}
                    orderItems={orderItems}
                    resetOrder={resetOrder}
                    tax={tax}
                    platformFee={platformFee}
                    // discount={discount}
                    orderTotal={orderTotal}
                    cartTotal={cartTotal}
                    prevOrderItemTotal={prevCartTotal}
                    additionalTotal={additionalTotal}
                    cartItemtax={cartItemtax}
                    cartItemplatformFee={cartItemplatformFee}
                    cartItemAdditionalTotal={cartItemAdditionalTotal}
                />
                :
                <NoOrderPanelContainer
                    openDialog={openDialog}
                    closeDialog={closeDialog}
                    isDesktop={isDesktop}
                    isMobile={isMobile}

                />
            }

            <OrderItemChefAssign
                open={assignChefOpen}
                onClose={() => {
                    setAssignChefOpen(false);
                    setAssignChefItems([]);
                    setAssignChefSelections({});
                    setResumeAfterChefAssign(null);
                }}
                orderItems={assignChefItems}
                selections={assignChefSelections}
                onChangeChef={handleAssignChefChange}
                onSubmit={handleAssignChefSubmit}
                loading={loader}
            />
        </>
    )
}

export default NewOrderItemPanel
